import type { Transport, TransportAssoc, WellAllocationRequest } from "@ots-frontend/ots-data";
import type { ContainerLevelRequest } from "../../tank-chart/connected/ConnectedContainerLevelDialog";
import type { ActivityEntry } from "./activity-types";

const DAY = 86_400_000;

/**
 * The tank and time to show readings for, as WaterGo picks them: a ticket's or pickup's tank from five days before
 * it to a day after, marked where it was written; a tank drop from a day either side of it.
 */
export const tankLevelRequest = (entry: ActivityEntry): ContainerLevelRequest | undefined => {
  const pickup = entry.pickup;
  if (pickup?.containerId && pickup.createDttm != null) {
    return {
      containerId: pickup.containerId,
      containerName: pickup.containerName,
      fromDttm: pickup.createDttm - 5 * DAY,
      toDttm: pickup.createDttm + DAY,
      markers: [{ time: pickup.createDttm, label: "Pickup" }],
    };
  }
  const drop = entry.containerVolumeDrop;
  if (drop?.containerId) {
    return { containerId: drop.containerId, containerName: drop.containerName, fromDttm: drop.startDttm - DAY, toDttm: (drop.endDttm ?? drop.startDttm) + DAY };
  }
  return undefined;
};

const isOperator = (transport: Transport | undefined) =>
  transport?.siteCompanyType === "Operator" || transport?.companyType === "Operator";

/**
 * A ticket's well allocation request, as WaterGo builds it: from arriving at the pickup to leaving the delivery, on
 * the operator's side of the ticket (the pickup, unless only the delivery is at an operator).
 */
export const wellAllocationRequest = (tta: TransportAssoc | undefined): WellAllocationRequest | undefined => {
  const pickup = tta?.pickupTransport;
  const delivery = tta?.deliveryTransport;
  if (!tta || !pickup || !delivery || !tta.regionId) return undefined;
  const side = !isOperator(pickup) && isOperator(delivery) ? delivery : pickup;
  const fromDttm = pickup.inDttm ?? pickup.createDttm;
  const toDttm = delivery.outDttm ?? delivery.createDttm;
  if (!side.containerId || fromDttm == null || toDttm == null) return undefined;
  return {
    fromDttm,
    toDttm,
    containerId: side.containerId,
    category: tta.category,
    regionId: tta.regionId,
    ...(side.serviceTypeId ? { serviceTypeId: side.serviceTypeId } : {}),
    ...(side.wellId ? { wellId: side.wellId } : {}),
    ...(tta.database ? { database: tta.database } : {}),
  };
};
